import {
    AmountInput,
    Button,
    FormField,
    InputGroup,
    Paragraph,
} from '@george-labs.com/design-system';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import type { FORM_FIELD_VARIANT } from '@george-labs.com/design-system/types/components/Form';
import classNames from 'classnames';
import type { AmountObject } from '@george-labs.com/design-system/types/components/Input';

import { useField } from '@luna/form2';
import { getFeatureConfig } from '@luna/feature-config';

import { shouldShowError } from './FieldErrorVisibilityService';
import type { FormFieldProps } from './types/FormFieldProps';

interface Props extends FormFieldProps {
    fieldName: string;
    isErrorVisible?: boolean;
    inputProps?: Partial<ComponentProps<typeof AmountInput>>;
    addButtonLabel: string;
    subtractButtonLabel: string;
    addButtonProps?: Omit<Partial<ComponentProps<typeof Button>>, 'children'>;
    subtractButtonProps?: Omit<Partial<ComponentProps<typeof Button>>, 'children'>;
    variant?: FORM_FIELD_VARIANT;
    changeStep?: number;
    shouldClearOnUnmount?: boolean;
}

const DECIMAL_BASE = 10;
const LABEL_COLUMN_WIDTH = 4;
const DEFAULT_PRECISION = 0;
const DEFAULT_CHANGE_STEP = 1;

export const parsePreciseNumber = (input: AmountObject): number => {
    const value = Number(input.value);
    const precision = input.precision ?? 0;

    return value / DECIMAL_BASE ** precision;
};

export const FormPlusMinusInputField = ({
    label,
    'data-cy': dataCy,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelPosition,
    labelClassName,
    fieldName,
    required,
    disabled,
    warning,
    info,
    alert,
    description,
    isErrorVisible = true,
    addon,
    addButtonLabel,
    subtractButtonLabel,
    variant = FormField.VARIANT.DEFAULT,
    changeStep = DEFAULT_CHANGE_STEP,
    inputProps = {},
    shouldClearOnUnmount,
    addButtonProps = {},
    subtractButtonProps = {},
    showErrorBeforeBlur,
    className,
    compact,
}: Props) => {
    const { decimalSeparator, thousandSeparator } = getFeatureConfig((f) => f.base);
    const [fieldProps, { error, touched, dirty }, { setValue, setTouched }] = useField<number>(
        fieldName,
        { shouldClearOnUnmount },
    );

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    const changeValue = useCallback(
        (operation: 'increase' | 'decrease') => {
            let newValue = 0;
            const prevValue = fieldProps.value ?? 0;

            if (operation === 'decrease') {
                if (prevValue > changeStep) {
                    newValue = prevValue - changeStep;
                }
            } else if (operation === 'increase') {
                newValue = prevValue + changeStep;
            }

            setValue(newValue);
            setTouched();

            if (inputProps.onInput) {
                inputProps.onInput({
                    value: newValue,
                    precision: inputProps.precision ?? DEFAULT_PRECISION,
                    currency: inputProps.currency,
                });
            }
        },
        [setTouched, setValue, fieldProps.value, changeStep, inputProps],
    );

    const increaseValue = () => changeValue('increase');
    const decreaseValue = () => changeValue('decrease');

    const handleAmountInput = (input: AmountObject) => {
        setValue(parsePreciseNumber(input));
        setTouched();

        if (inputProps.onInput) {
            inputProps.onInput(input);
        }
    };

    return (
        <FormField
            variant={variant}
            label={label}
            error={
                isFieldErrorVisible && <Paragraph data-cy={`${dataCy}-error`}>{error}</Paragraph>
            }
            labelColumnWidth={labelColumnWidth}
            labelPosition={labelPosition}
            disabled={disabled}
            warning={warning}
            info={info}
            alert={alert}
            addon={addon}
            description={description}
            required={required}
            className={className}
            labelClassName={labelClassName}
            compact={compact}
            input={
                <InputGroup
                    prepend={
                        <Button
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...subtractButtonProps}
                            icon={Button.ICON.MINUS}
                            fullWidth={Button.FULL_WIDTH.NEVER}
                            hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                            onClick={decreaseValue}
                            data-cy={dataCy ? `button-${dataCy}-minus` : undefined}
                        >
                            {subtractButtonLabel}
                        </Button>
                    }
                    append={
                        <Button
                            // eslint-disable-next-line react/jsx-props-no-spreading
                            {...addButtonProps}
                            icon={Button.ICON.PLUS}
                            fullWidth={Button.FULL_WIDTH.NEVER}
                            hiddenLabel={Button.HIDDEN_LABEL.ALWAYS}
                            onClick={increaseValue}
                            data-cy={dataCy ? `button-${dataCy}-plus` : undefined}
                        >
                            {addButtonLabel}
                        </Button>
                    }
                >
                    <AmountInput
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...fieldProps}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...inputProps}
                        value={fieldProps.value}
                        precision={inputProps.precision ?? DEFAULT_PRECISION}
                        decimalSeparator={decimalSeparator}
                        thousandSeparator={thousandSeparator}
                        onInput={handleAmountInput}
                        className={classNames({
                            'text-center': variant === FormField.VARIANT.DEFAULT,
                        })}
                        data-cy={dataCy ? `${dataCy}-input` : undefined}
                    />
                </InputGroup>
            }
        />
    );
};

FormPlusMinusInputField.LABEL_POSITION = FormField.LABEL_POSITION;
FormPlusMinusInputField.VARIANT = FormField.VARIANT;
